import { safeStorage } from '@utils/storage'
import type { StoreApi } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { createStore } from 'zustand/vanilla'

/** The answer maps a questionnaire fills in. */
export interface AnswerMaps {
  answers: Record<string, string | string[]>
  exitAnswers: Record<string, string | string[]>
  flaggedAnswers: Record<string, string[]>
  /** A follow-up carried over with no answer is hidden by storing null against its hash. */
  hiddenAnswers: Record<string, string | string[] | null>
  /** A carried answer without an option hash stores null, so it matches its option by label. */
  optionHashes: Record<string, string | string[] | null>
  /** Heart rate from a shenai question's scan. Kept apart, as the answer must stay a "120/80" reading. */
  pulses: Record<string, ScanPulse>
}

/** A pulse and the reading it was measured with. It counts only while the answer is still that reading. */
export interface ScanPulse {
  reading: string
  bpm: number
}

/** One flow's answers and the actions that set them. */
export interface AnswersState extends AnswerMaps {
  setSingleAnswer: (questionHash: string, answerValue: string | string[]) => void
  setExitAnswers: (questionHash: string, answerValue: string | string[]) => void
  setFlaggedAnswers: (questionHash: string, answerValue: string[]) => void
  setHiddenAnswers: (questionHash: string, answerValue: string | string[] | null) => void
  setOptionHash: (questionHash: string, optionHash: string | string[] | null) => void
  deleteAnswer: (questionHash: string) => void
  /** Records a collected scan: the "120/80" answer and its pulse. */
  setShenReading: (questionHash: string, answer: string, pulse: number | null) => void
}

export interface ScopedAnswersState extends AnswersState {
  /** What this answer set belongs to. Persisted so a stale set is discarded. */
  owner: string
  reset: () => void
}

export type AnswersStore = ReturnType<typeof createAnswersStore>

export const emptyAnswers = (): AnswerMaps => ({
  answers: {},
  exitAnswers: {},
  flaggedAnswers: {},
  hiddenAnswers: {},
  optionHashes: {},
  pulses: {}
})

/** A request's owner is its `qreq_…` hash. Consultations and renewals are prefixed so each kind can be cleared together. */
const CONSULTATION_PREFIX = 'consultation_'
const RENEWAL_PREFIX = 'renewal_'

export const consultationOwner = (questionnaireHash: string) => `${CONSULTATION_PREFIX}${questionnaireHash}`
export const renewalOwner = (orderHash: string) => `${RENEWAL_PREFIX}${orderHash}`

export const isConsultationOwner = (owner: string) => owner.startsWith(CONSULTATION_PREFIX)
export const isRenewalOwner = (owner: string) => owner.startsWith(RENEWAL_PREFIX)

/** One storage key per owner. The prefix predates consultations and renewals, and `public/iframe.js` matches these keys on a host logout. */
const ANSWERS_STORAGE_PREFIX = 'questionnaire_request_answers'

export const answersStorageKey = (owner: string) => `${ANSWERS_STORAGE_PREFIX}_${owner}`

/** Owners with a stored set and when each was last answered, since `safeStorage` cannot be listed. */
export const ANSWERS_INDEX_KEY = `${ANSWERS_STORAGE_PREFIX}_index`

/** A set is dropped 30 days after its last answer. */
const ANSWER_SET_TTL = 30 * 24 * 60 * 60 * 1000

interface IndexEntry {
  owner: string
  seen: number
}

export const readIndex = (): IndexEntry[] => {
  try {
    const stored: unknown = JSON.parse(safeStorage.getItem(ANSWERS_INDEX_KEY) || '[]')
    if (!Array.isArray(stored)) return []

    return stored.flatMap<IndexEntry>(entry => {
      // A bare owner is the index's first format, dated to now so its set ages out from here.
      if (typeof entry === 'string') return [{ owner: entry, seen: Date.now() }]

      return typeof entry?.owner === 'string' && typeof entry?.seen === 'number' ? [entry] : []
    })
  } catch {
    return []
  }
}

const writeIndex = (entries: IndexEntry[]) => safeStorage.setItem(ANSWERS_INDEX_KEY, JSON.stringify(entries))

const isFresh = (entry: IndexEntry, now: number) => now - entry.seen < ANSWER_SET_TTL

/** Removes a stored set. Only an existing key is removed, as an embedded host keeps a removal as a null. */
const removeSet = (owner: string) => {
  if (safeStorage.getItem(answersStorageKey(owner)) !== null) safeStorage.removeItem(answersStorageKey(owner))
}

/** Whether the owner has a set answered within the window. */
export const hasFreshSet = (owner: string) => {
  const now = Date.now()
  return readIndex().some(entry => entry.owner === owner && isFresh(entry, now))
}

/** Indexes a set when it is opened, and sweeps out whatever has aged past the window, its own expired answers included. Opening alone does not renew a set. */
export const rememberOwner = (owner: string) => {
  const now = Date.now()
  const entries = readIndex()

  const freshEntries = entries.filter(entry => isFresh(entry, now))
  const freshOwners = new Set(freshEntries.map(entry => entry.owner))

  // An owner with any fresh entry keeps its set.
  entries.filter(entry => !freshOwners.has(entry.owner)).forEach(entry => removeSet(entry.owner))

  const own = freshEntries.filter(entry => entry.owner === owner)
  const seen = own.length ? Math.max(...own.map(entry => entry.seen)) : now
  writeIndex([...freshEntries.filter(entry => entry.owner !== owner), { owner, seen }])
}

/** Restarts the window for a set the patient has just answered. */
const touchOwner = (owner: string) => {
  const now = Date.now()
  const entries = readIndex()

  // A minute is nothing against 30 days, and skipping saves a storage write on every keystroke.
  if (entries.some(entry => entry.owner === owner && now - entry.seen < 60_000)) return

  writeIndex([...entries.filter(entry => entry.owner !== owner), { owner, seen: now }])
}

/** Removes the sets and their index entries. */
export const forgetOwners = (owners: string[]) => {
  owners.forEach(removeSet)
  writeIndex(readIndex().filter(entry => !owners.includes(entry.owner)))
}

type SetAnswers = StoreApi<AnswersState>['setState']

/**
 * The question's pulse while its answer is still the scanned reading, otherwise null. A carried, retyped or
 * restored answer never picks up the pulse of another reading, whichever writer put it there.
 */
export const scanPulse = ({ answers, pulses }: Pick<AnswerMaps, 'answers' | 'pulses'>, questionHash: string): number | null => {
  const entry = pulses?.[questionHash]
  return entry && answers?.[questionHash] === entry.reading ? entry.bpm : null
}

/** Every question's pulse that still belongs to its answer, as sent with the answers. */
export const scanPulses = (maps: Pick<AnswerMaps, 'answers' | 'pulses'>): Record<string, number> =>
  Object.fromEntries(
    Object.keys(maps.pulses ?? {}).flatMap(questionHash => {
      const bpm = scanPulse(maps, questionHash)
      return bpm == null ? [] : [[questionHash, bpm]]
    })
  )

export const answerActions = (set: SetAnswers, get: () => AnswersState) => ({
  setSingleAnswer: (questionHash: string, answerValue: string | string[]) => set({ answers: { ...get().answers, [questionHash]: answerValue } }),
  setExitAnswers: (questionHash: string, answerValue: string | string[]) => set({ exitAnswers: { ...get().exitAnswers, [questionHash]: answerValue } }),
  setFlaggedAnswers: (questionHash: string, answerValue: string[]) => set({ flaggedAnswers: { ...get().flaggedAnswers, [questionHash]: answerValue } }),
  setHiddenAnswers: (questionHash: string, answerValue: string | string[] | null) =>
    set({ hiddenAnswers: { ...get().hiddenAnswers, [questionHash]: answerValue } }),
  setOptionHash: (questionHash: string, optionHash: string | string[] | null) => set({ optionHashes: { ...get().optionHashes, [questionHash]: optionHash } }),
  deleteAnswer: (questionHash: string) => {
    const { [questionHash]: _answer, ...answers } = get().answers
    const { [questionHash]: _flags, ...flaggedAnswers } = get().flaggedAnswers
    const { [questionHash]: _option, ...optionHashes } = get().optionHashes
    set({ answers, flaggedAnswers, optionHashes })
  },
  setShenReading: (questionHash: string, answer: string, pulse: number | null) => {
    // A scan with no pulse drops the earlier one, which could otherwise match a repeat of the same reading.
    const { [questionHash]: _earlier, ...pulses } = get().pulses ?? {}
    set({
      answers: { ...get().answers, [questionHash]: answer },
      pulses: pulse == null ? pulses : { ...pulses, [questionHash]: { reading: answer, bpm: pulse } }
    })
  }
})

/** A persisted answer set. Hydration is left to the caller, as hydrating during render splits the server and first client pass. */
export const createAnswersStore = (owner: string) =>
  createStore<ScopedAnswersState>()(
    persist(
      (set, get) => ({
        owner,
        ...emptyAnswers(),
        reset: () => set(emptyAnswers()),
        ...answerActions(set, get)
      }),
      {
        name: answersStorageKey(owner),
        storage: createJSONStorage(() => ({
          ...safeStorage,
          setItem: (key: string, value: string) => {
            safeStorage.setItem(key, value)
            touchOwner(owner)
          }
        })),
        skipHydration: true,
        partialize: ({ owner, answers, exitAnswers, flaggedAnswers, hiddenAnswers, optionHashes, pulses }) => ({
          owner,
          answers,
          exitAnswers,
          flaggedAnswers,
          hiddenAnswers,
          optionHashes,
          pulses
        }),
        // A payload naming another owner is discarded.
        merge: (persisted, current) => {
          const stored = persisted as Partial<ScopedAnswersState> | undefined

          return stored?.owner === current.owner ? { ...current, ...stored } : current
        }
      }
    )
  )

/** Stores a set without opening it, in the format its store reads back. */
export const writeAnswerSet = (owner: string, maps: AnswerMaps) => {
  // Indexed first, so an expired entry for the owner is swept before the new answers are written.
  rememberOwner(owner)
  createAnswersStore(owner).setState(maps)
}

export type CarriedAnswers = Pick<AnswerMaps, 'answers' | 'hiddenAnswers' | 'optionHashes'>

/**
 * The answers a reorder or renewal carries over, by question hash. A valid answered question is answered and hidden,
 * a follow-up with no answer stays hidden, and a top-level question with no answer stays visible.
 */
export const carriedAnswers = (...questionnaires: Record<string, any>[]): CarriedAnswers => {
  const carried: CarriedAnswers = { answers: {}, hiddenAnswers: {}, optionHashes: {} }

  for (const questionnaire of questionnaires) {
    for (const [hash, question] of Object.entries(questionnaire ?? {})) {
      if (!question?.is_valid) continue

      if (question.answer) {
        carried.answers[hash] = question.answer
        carried.hiddenAnswers[hash] = question.answer
        if (question.answer_hash) carried.optionHashes[hash] = question.answer_hash
      } else if (question.is_followup) {
        carried.hiddenAnswers[hash] = null
      }
    }
  }

  return carried
}
